import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ModalDialog } from '../features/job-roles/components/ModalDialog';
import type { SubscriptionRestriction } from './subscriptionRestriction';

export const COMPARE_PLANS_PATH = '/settings/subscription';

export interface LockedFeatureDialogProps {
  /** The feature the person asked for, by its catalog display name. */
  featureName: string;
  /** The plan that includes it. Null when the server did not name one. */
  requiredPlan: string | null;
  /** The company's current plan. Null when there is none. */
  currentPlan: string | null;
  /** What changes if the plan changes, in the catalog's own words (the feature's upgrade message). A neutral sentence is used when absent. */
  message?: string | null;
  onClose: () => void;
  /** Called when Compare plans is chosen (the dialog also closes). The link itself goes to the subscription page. */
  onComparePlans?: () => void;
  /** An extra, caller-specific action shown before Compare plans (for example "Save as not active for payroll"). */
  extraActions?: ReactNode;
}

/**
 * The one explanation of a plan-gated feature the company's plan does not include, used by the padlock affordance (`EntitlementGate`)
 * and by every screen the server can refuse with `SUBSCRIPTION_UPGRADE_REQUIRED`. It names the feature, the plan that includes it and
 * the current plan, says what changes, and is honest that nothing happens here: a plan change is made only on the subscription page by
 * an authorized billing user. It never performs an upgrade and never offers one for a subscription that is not in force (that is a
 * different message, and is not shown with this dialog).
 *
 * Built on `ModalDialog`, which supplies the accessible-dialog behavior: labelled by its title, described by the "what changes"
 * text, focus moves in on open, Tab and Shift+Tab stay inside, Escape closes it, and focus returns to what opened it.
 */
export function LockedFeatureDialog({
  featureName, requiredPlan, currentPlan, message, onClose, onComparePlans, extraActions,
}: LockedFeatureDialogProps) {
  const uid = useId();
  const needed = requiredPlan ?? 'a higher plan';
  const whatChanges = message && message.trim().length > 0
    ? message
    : `${featureName} is included in ${needed}. Compare plans to see what each plan includes.`;
  const close = () => onClose();

  return (
    <ModalDialog
      title={`${featureName} needs ${needed}`}
      size="sm"
      describedBy={`${uid}-what`}
      onClose={close}
      footer={(
        <>
          {extraActions}
          <Link
            className="jr-btn jr-btn--primary lfd-link"
            to={COMPARE_PLANS_PATH}
            onClick={() => {
              onComparePlans?.();
              onClose();
            }}
          >
            Compare plans
          </Link>
          <button type="button" className="jr-btn jr-btn--secondary" data-autofocus onClick={close}>Close</button>
        </>
      )}
    >
      <dl className="lfd-facts">
        <div><dt>Feature</dt><dd>{featureName}</dd></div>
        <div><dt>Included in</dt><dd>{requiredPlan ?? 'A higher plan'}</dd></div>
        <div><dt>Your current plan</dt><dd>{currentPlan ?? 'No plan selected'}</dd></div>
      </dl>
      <div>
        <h4 className="lfd-heading">What changes</h4>
        <p id={`${uid}-what`}>{whatChanges}</p>
      </div>
      <p className="muted lfd-note">
        Nothing changes until an authorized billing user confirms a plan change. Only your company administrator can change the plan.
      </p>
    </ModalDialog>
  );
}

/**
 * The dialog for a server refusal (`SUBSCRIPTION_UPGRADE_REQUIRED`): the refusal's own feature name, plans and message, never a blank
 * or generic error. `fallbackFeatureName` is used only when the server named no feature.
 */
export function RestrictionLockedDialog({
  restriction, fallbackFeatureName = 'This feature', onClose, extraActions,
}: {
  restriction: SubscriptionRestriction;
  fallbackFeatureName?: string;
  onClose: () => void;
  extraActions?: ReactNode;
}) {
  return (
    <LockedFeatureDialog
      featureName={restriction.featureName ?? fallbackFeatureName}
      requiredPlan={restriction.requiredPlan}
      currentPlan={restriction.currentPlan}
      message={restriction.message ?? null}
      onClose={onClose}
      extraActions={extraActions}
    />
  );
}
